/**
 * Display helpers for WasteSense sites. Centralised so the sites
 * panel and the map popups all show sites the same way.
 *
 * The upstream pipeline emits titles as `"<address> — <suburb> — <state>"`,
 * but operators read them by suburb first ("everything Glenroy
 * clustered together, then Brunswick…"), so we flip the order on
 * display.
 */
import type { WasteSenseMapSite } from "@visioni/a3s-reports/client-messages";

export interface SiteHeading {
  /** Display string: `"<suburb> — <address>"` (or just `<address>` when no suburb). */
  primary: string;
  /** Lowercased suburb, for stable case-insensitive sorting. */
  suburbKey: string;
  /** Lowercased address, used as the secondary sort key. */
  addressKey: string;
}

const TITLE_DELIMITER = " — ";

export function parseSiteTitle(title: string): SiteHeading {
  const parts = title.split(TITLE_DELIMITER).map((s) => s.trim());
  const address = parts[0] ?? title;
  const suburb = parts[1] ?? "";

  // State (parts[2]) is intentionally dropped from the display — redundant
  // inside a single-tenant report and adds visual noise to dense rows.
  const primary = suburb ? `${suburb}${TITLE_DELIMITER}${address}` : address;

  return {
    primary,
    suburbKey: suburb.toLowerCase(),
    addressKey: address.toLowerCase(),
  };
}

/** Convenience wrapper — returns just the display string. */
export function formatSiteHeading(title: string): string {
  return parseSiteTitle(title).primary;
}

/**
 * Same heading formatter for the map's popup, which carries `site_title`
 * rather than the bare title string.
 */
export function formatMapSiteHeading(site: Pick<WasteSenseMapSite, "site_title">): string {
  return formatSiteHeading(site.site_title);
}
